import { zodResolver } from '@hookform/resolvers/zod';
import EditIcon from '@mui/icons-material/Edit';
import {
  Box,
  Button,
  Divider,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  Typography,
} from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { z } from 'zod';
import {
  AddressFields,
  EmailField,
  EntityProfilePage,
  ManufacturerField,
  PhoneNumberField,
  SelectField,
  StatusIndicator,
  TextInput,
} from '../../components/form';
import { useAppSelector } from '../../store/hooks';
import { Dealer, DealerLocation } from '../../types';
import {
  address1Schema,
  address2Schema,
  citySchema,
  emailSchema,
  formatPhoneNumber,
  logError,
  optionalPhoneNumberSchema,
  phoneNumberSchema,
  preventPaste,
  showToast,
  stateSchema,
  unformatPhoneNumber,
  zipCodeSchema,
} from '../../utils';
import { AddLocationAddressDialog, EditLocationAddressDialog } from '../location';
import { useGetLocationsQuery } from '../location/locations-api-slice';
import { useManufacturerData } from '../manufacturer/use-manufacturer-data';
import {
  selectManageDealers,
  selectManageManufacturers,
  selectManageShippingLocations,
} from '../user';
import {
  useAddDealerMutation,
  useGetDealerByIdQuery,
  useGetDocumentTiersQuery,
  useUpdateDealerMutation,
} from './dealer-api-slice';

// Define Zod schema for form validation
const dealerFormSchema = z.object({
  name: z.string().min(1, 'Dealer name is required').max(250, 'Dealer name is too long'),
  code: z.string().min(1, 'Code is required').max(8, 'Code can be no longer than 8 characters'),
  address1: address1Schema,
  address2: address2Schema,
  city: citySchema,
  state: stateSchema,
  zip: zipCodeSchema,
  phone: phoneNumberSchema,
  fax: optionalPhoneNumberSchema,
  email: emailSchema,
  status: z.boolean(),
  documentTier: z.string().min(1, 'Document tier is required'),
  manufacturerId: z.number().min(1, 'Manufacturer is required'),
});

// Extract type from Zod schema
type DealerFormValues = z.infer<typeof dealerFormSchema>;

// Section component with title integrated into the border
const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <Box
    sx={{
      position: 'relative',
      height: '100%',
      border: '1px solid',
      borderColor: 'divider',
      borderRadius: 1,
      pt: 3,
      pb: 2,
      px: 2,
    }}
  >
    <Typography
      variant="subtitle1"
      component="h2"
      fontWeight="500"
      sx={{
        position: 'absolute',
        top: 0,
        transform: 'translateY(-50%)',
        backgroundColor: 'background.paper',
        px: 1,
        ml: 1,
      }}
    >
      {title}
    </Typography>
    {children}
  </Box>
);

const DealerProfilePage: React.FC = () => {
  const { id } = useParams<{ id?: string }>();
  const isEditMode = !!id;

  // Get current user from Redux store
  const currentUser = useAppSelector(state => state.user.currentUser);
  const canManageManufacturers = useAppSelector(selectManageManufacturers);
  const canManageDealers = useAppSelector(selectManageDealers);
  const canManageShippingLocations = useAppSelector(selectManageShippingLocations);
  const currentUserManufacturerId = currentUser?.manufacturerId;

  // Shipping location dialog state
  const [showAddLocationDialog, setShowAddLocationDialog] = useState(false);
  const [showEditLocationDialog, setShowEditLocationDialog] = useState(false);
  const [editingLocation, setEditingLocation] = useState<DealerLocation | null>(null);

  const handleAddLocation = useCallback(() => {
    setShowAddLocationDialog(false);
  }, []);

  const handleEditLocation = useCallback((location: DealerLocation) => {
    setEditingLocation(location);
    setShowEditLocationDialog(true);
  }, []);

  const handleEditLocationSave = useCallback(() => {
    setShowEditLocationDialog(false);
    setEditingLocation(null);
  }, []);

  // Get dealer data
  const {
    data: dealer,
    isLoading: isDealerLoading,
    error,
    refetch: refetchDealer,
  } = useGetDealerByIdQuery(id ?? '', {
    skip: !id,
    refetchOnMountOrArgChange: true,
  });

  // Use the shared manufacturer data hook
  const { manufacturers, isLoading: isLoadingManufacturers } = useManufacturerData({
    isEditMode,
    profileManufacturerId: dealer?.manufacturerId,
    currentUserManufacturerId,
  });

  const [updateDealer, { isLoading: isUpdating }] = useUpdateDealerMutation();
  const [addDealer, { isLoading: isAdding }] = useAddDealerMutation();
  const { data: documentTiers = [], isLoading: isDocumentTiersLoading } =
    useGetDocumentTiersQuery();

  // Fetch locations for the dealer
  const {
    data: locations = [],
    isLoading: isLoadingLocations,
    error: locationsError,
  } = useGetLocationsQuery(dealer?.id ?? 0, {
    skip: !dealer?.id || dealer.id === 0,
  });

  const isLoading = isDealerLoading || isLoadingManufacturers;
  const isInProcess = isUpdating || isAdding || isDocumentTiersLoading;

  // Set up React Hook Form with Zod resolver
  const form = useForm<DealerFormValues>({
    defaultValues: {
      name: '',
      code: '',
      address1: '',
      address2: '',
      city: '',
      state: '',
      zip: '',
      phone: '',
      fax: '',
      email: '',
      status: true,
      documentTier: '',
      manufacturerId: (!canManageManufacturers && currentUserManufacturerId) || 0,
    },
    resolver: zodResolver(dealerFormSchema),
    mode: 'onTouched',
  });

  const {
    control,
    reset,
    setValue,
    formState: { isDirty, isValid },
  } = form;

  // Handle form submission with React Hook Form
  const onSubmit = async (data: DealerFormValues) => {
    try {
      // Process phone number to store only digits
      const processedData = {
        ...data,
        phone: unformatPhoneNumber(data.phone),
        fax: data.fax && data.fax.trim() !== '' ? unformatPhoneNumber(data.fax) : undefined,
        address2: data.address2 && data.address2.trim() !== '' ? data.address2 : undefined,
      };

      if (id) {
        const { manufacturer: _manufacturer, ...dealerDataToUpdate } = processedData as Dealer;
        await updateDealer({
          id,
          dealerData: dealerDataToUpdate,
        }).unwrap();
        showToast('Dealer successfully updated', 'success');
      } else {
        const newDealer: Omit<Dealer, 'id'> = {
          name: processedData.name,
          code: processedData.code,
          address1: processedData.address1,
          address2: processedData.address2,
          city: processedData.city,
          state: processedData.state,
          zip: processedData.zip,
          phone: processedData.phone,
          fax: processedData.fax,
          email: processedData.email,
          status: processedData.status,
          documentTier: processedData.documentTier,
          manufacturerId: processedData.manufacturerId,
        };

        await addDealer({
          dealerData: newDealer,
        }).unwrap();
        showToast('Dealer successfully created', 'success');
      }

      return Promise.resolve();
    } catch (error) {
      logError('Error updating dealer:', error);
      return Promise.reject(error instanceof Error ? error : new Error(String(error)));
    }
  };

  useEffect(() => {
    if (id) {
      refetchDealer();
    }
  }, [id, refetchDealer]);

  // For new dealer creation, set manufacturer ID to current user's manufacturer for non-admin users
  useEffect(() => {
    if (!isEditMode && !canManageManufacturers && currentUserManufacturerId) {
      // Set the manufacturer ID to the current user's manufacturer for non-admin users
      setValue('manufacturerId', currentUserManufacturerId);
    }
  }, [isEditMode, canManageManufacturers, currentUserManufacturerId, setValue]);

  useEffect(() => {
    if (dealer) {
      const effectiveManufacturerId =
        !canManageManufacturers && currentUserManufacturerId
          ? currentUserManufacturerId
          : dealer?.manufacturerId;

      reset({
        name: dealer.name,
        code: dealer.code,
        address1: dealer.address1,
        address2: dealer.address2 ?? '',
        city: dealer.city,
        state: dealer.state,
        zip: dealer.zip,
        phone: dealer.phone,
        fax: dealer.fax ?? '',
        email: dealer.email,
        status: dealer.status,
        documentTier: dealer.documentTier,
        manufacturerId: effectiveManufacturerId ?? undefined,
      });
    }
  }, [dealer, reset, canManageManufacturers, currentUserManufacturerId]);

  // Find the manufacturer name from the dealer's manufacturerId or manufacturer field
  const getManufacturerName = () => {
    if (dealer) {
      if (dealer.manufacturerId) {
        return manufacturers.find(m => m.id === dealer.manufacturerId)?.licenseeName ?? 'Unknown';
      } else if (dealer.manufacturer) {
        return dealer.manufacturer;
      }
    }

    if (currentUserManufacturerId && !canManageManufacturers) {
      return manufacturers.find(m => m.id === currentUserManufacturerId)?.licenseeName ?? 'Unknown';
    }

    return 'None';
  };

  return (
    <EntityProfilePage
      title="Dealer Profile"
      entity={dealer}
      isLoading={isLoading}
      error={error}
      isSaving={isInProcess}
      form={form}
      hasChanges={isDirty}
      isValid={isValid}
      isReadOnly={!canManageDealers}
      onSubmit={onSubmit}
      pageWidth="xl"
    >
      <Grid container spacing={2}>
        {/* Top Row: Account Info and Billing Address */}
        <Grid item xs={12} md={6}>
          <Section title="Dealer Information">
            <Grid container spacing={2}>
              {/* Manufacturer field */}
              {canManageManufacturers && (
                <ManufacturerField
                  control={control}
                  name="manufacturerId"
                  manufacturers={manufacturers}
                  isReadOnly={!canManageManufacturers}
                  getManufacturerName={getManufacturerName}
                  isLoading={isLoadingManufacturers}
                />
              )}

              {/* Dealer Name */}
              <Grid item xs={8} sm={9}>
                <TextInput
                  name="name"
                  control={control}
                  label="Dealer Name"
                  isReadOnly={!canManageDealers}
                  required={true}
                />
              </Grid>

              {/* Status toggle */}
              <Grid
                item
                xs={4}
                sm={3}
                sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}
              >
                <StatusIndicator name="status" control={control} isReadOnly={!canManageDealers} />
              </Grid>

              {/* Code */}
              <Grid item xs={12} sm={6}>
                <TextInput
                  name="code"
                  control={control}
                  label="Code"
                  isReadOnly={!canManageDealers}
                  required={true}
                />
              </Grid>

              {/* Phone */}
              <Grid item xs={12} sm={6}>
                <PhoneNumberField
                  name="phone"
                  control={control}
                  formatPhoneNumber={formatPhoneNumber}
                  label="Phone Number"
                  required
                  isReadOnly={!canManageDealers}
                />
              </Grid>

              {/* Fax */}
              <Grid item xs={12} sm={6}>
                <PhoneNumberField
                  name="fax"
                  control={control}
                  formatPhoneNumber={formatPhoneNumber}
                  label="Fax Number"
                  isReadOnly={!canManageDealers}
                />
              </Grid>

              {/* Email */}
              <Grid item xs={12} sm={6}>
                <EmailField
                  emailName="email"
                  control={control}
                  handlePaste={preventPaste}
                  isReadOnly={!canManageDealers}
                />
              </Grid>
            </Grid>
          </Section>
        </Grid>

        <Grid item xs={12} md={6}>
          <Section title="Billing Address">
            <Grid container spacing={2}>
              <AddressFields
                control={control}
                address1Name="address1"
                address2Name="address2"
                cityName="city"
                stateName="state"
                zipName="zip"
                isReadOnly={!canManageDealers}
              />
            </Grid>
          </Section>
        </Grid>

        {/* Bottom Row: Document Tiers, Sales Stats, Shipping Locations */}
        <Grid item xs={12} md={4}>
          <Section title="Document Tiers">
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <SelectField
                  name="documentTier"
                  control={control}
                  label="Document Tier"
                  options={documentTiers.map((tier: string) => ({ value: tier, label: tier }))}
                  isReadOnly={!canManageDealers}
                  required={true}
                />
              </Grid>
            </Grid>
          </Section>
        </Grid>

        <Grid item xs={12} md={4}>
          <Section title="Sales Stats">
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
              }}
            >
              <Typography variant="body1" color="text.secondary">
                Sales statistics will be displayed here
              </Typography>
            </Box>
          </Section>
        </Grid>

        <Grid item xs={12} md={4}>
          <Section title="Shipping Locations">
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
              <Box sx={{ flex: 1 }}>
                {isLoadingLocations ? (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      py: 2,
                    }}
                  >
                    <Typography variant="body1" color="text.secondary">
                      Loading locations...
                    </Typography>
                  </Box>
                ) : locationsError ? (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      py: 2,
                    }}
                  >
                    <Typography variant="body1" color="error">
                      Error loading shipping locations
                    </Typography>
                  </Box>
                ) : locations.length === 0 ? (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      py: 2,
                    }}
                  >
                    <Typography variant="body1" color="text.secondary">
                      No shipping locations found
                    </Typography>
                  </Box>
                ) : (
                  <List dense disablePadding>
                    {locations.map((location, index) => (
                      <React.Fragment key={location.id}>
                        <ListItem disablePadding>
                          <ListItemText
                            primary={location.name}
                            secondary={
                              <Box component="div">
                                <Typography variant="body2" color="text.secondary" component="span">
                                  {[
                                    location.address1,
                                    location.address2,
                                    location.city,
                                    location.state,
                                    location.zip,
                                  ]
                                    .filter(Boolean)
                                    .join(', ')}
                                </Typography>
                              </Box>
                            }
                          />
                          {canManageShippingLocations && (
                            <ListItemSecondaryAction>
                              <IconButton
                                edge="end"
                                size="small"
                                aria-label="edit"
                                onClick={() => handleEditLocation(location)}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </ListItemSecondaryAction>
                          )}
                        </ListItem>
                        {index < locations.length - 1 && <Divider />}
                      </React.Fragment>
                    ))}
                  </List>
                )}
              </Box>
              {canManageShippingLocations && isEditMode && dealer?.id && (
                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    variant="outlined"
                    color="primary"
                    size="small"
                    onClick={() => setShowAddLocationDialog(true)}
                  >
                    Add Shipping Address
                  </Button>
                </Box>
              )}
            </Box>
          </Section>
        </Grid>
      </Grid>

      {dealer?.id && (
        <AddLocationAddressDialog
          open={showAddLocationDialog}
          onClose={() => setShowAddLocationDialog(false)}
          onSave={handleAddLocation}
          dealerId={dealer.id}
        />
      )}

      {editingLocation && (
        <EditLocationAddressDialog
          open={showEditLocationDialog}
          onClose={() => {
            setShowEditLocationDialog(false);
            setEditingLocation(null);
          }}
          onSave={handleEditLocationSave}
          location={editingLocation}
        />
      )}
    </EntityProfilePage>
  );
};

export default DealerProfilePage;
